
import { arrow, computePosition, flip, offset, shift } from '@floating-ui/dom'
import DimOverlay from '@marketplace/components/dim-overlay.vue'
import CustomTooltip from '@marketplace/components/pulse/custom-tooltip.vue'

export default {
    name: 'BaseTooltip',

    components: {
        DimOverlay,
        CustomTooltip,
    },

    props: {
        placement: {
            type: String,
            default: 'bottom',
        },

        content: {
            type: String,
            required: true,
        },
        hideOverlay: {
            type: Boolean,
            default: false,
        },
        referenceClass: {
            type: String,
            default: 'mx-1',
        },
        iconClass: {
            type: String,
            default: 'text-14',
        },
    },

    data() {
        return {
            isTooltipVisible: false,
        }
    },

    computed: {
        isMobile() {
            return this.$breakpoint.includes('mobile')
        },
    },

    methods: {
        showTooltip() {
            this.hoverTimer = setTimeout(async () => {
                this.isTooltipVisible = true
                await this.$nextTick()
                if (this.$refs.referenceRef && this.$refs.tooltipRef) {
                    if (
                        typeof this.$refs.tooltipRef.getFloatingRef ===
                        'function'
                    ) {
                        const floatingRef =
                            this.$refs.tooltipRef.getFloatingRef()

                        if (floatingRef) {
                            if (this.isMobile) {
                                Object.assign(floatingRef.style, {
                                    position: 'fixed',
                                    bottom: '0',
                                    left: '0',
                                    right: '0',
                                })
                            } else {
                                // Reset the styles when not in mobile view.
                                Object.assign(floatingRef.style, {
                                    position: '',
                                    bottom: '',
                                    left: '',
                                    right: '',
                                })
                                this.calculateTooltipPosition()
                            }
                        } else {
                            console.error(
                                'getFloatingRef() did not return a valid reference'
                            )
                        }
                    } else {
                        console.error(
                            'tooltipRef does not have a getFloatingRef() method'
                        )
                    }
                } else {
                    console.error('DOM not yet available')
                    this.isTooltipVisible = false
                }
            }, 250)
        },

        hideTooltip() {
            if (!this.isTooltipVisible) {
                return
            }
            this.isTooltipVisible = false
        },

        async calculateTooltipPosition() {
            if (!this.isTooltipVisible) return
            await this.$nextTick()

            const arrowRef = this.$refs.tooltipRef.getArrowRef()
            const floatingRef = this.$refs.tooltipRef.getFloatingRef()
            const hiddenDivRef = this.$refs.tooltipRef.getHiddenDivRef()
            if (
                arrowRef &&
                floatingRef &&
                hiddenDivRef &&
                this.$refs.referenceRef
            ) {
                const { x, y, middlewareData, placement } =
                    await computePosition(
                        this.$refs.referenceRef,
                        floatingRef,
                        {
                            placement: this.placement,
                            middleware: [
                                offset(8),
                                flip(),
                                shift({ padding: 5 }),
                                arrow({ element: arrowRef }),
                            ],
                        }
                    )

                Object.assign(floatingRef.style, {
                    left: `${x}px`,
                    top: `${y}px`,
                })

                const { x: arrowX, y: arrowY } = middlewareData.arrow

                const mainPlacement = placement.split('-')[0]
                const opposedSide = {
                    top: 'bottom',
                    bottom: 'top',
                    left: 'right',
                    right: 'left',
                }[mainPlacement]

                Object.assign(arrowRef.style, {
                    left: arrowX ? `${arrowX}px` : '',
                    top: arrowY ? `${arrowY}px` : '',
                    bottom: '',
                    right: '',
                    [opposedSide]: '-4px',
                })

                // Adjust hiddenDivRef based on placement
                let extraStyle = {}
                switch (mainPlacement) {
                    case 'top':
                        extraStyle = {
                            height: '30px',
                            width: `${floatingRef.offsetWidth}px`,
                            bottom: '-30px',
                        }
                        break
                    case 'bottom':
                        extraStyle = {
                            height: '30px',
                            width: `${floatingRef.offsetWidth}px`,
                            top: '-30px',
                        }
                        break
                    case 'left':
                        extraStyle = {
                            height: `${floatingRef.offsetHeight}px`,
                            width: '30px',
                            right: '-30px',
                        }
                        break
                    case 'right':
                        extraStyle = {
                            height: `${floatingRef.offsetHeight}px`,
                            width: '30px',
                            left: '-30px',
                        }
                        break
                }

                Object.assign(hiddenDivRef.style, extraStyle)
            }
        },
        clearHoverTimer() {
            if (this.hoverTimer) {
                clearTimeout(this.hoverTimer)
            }
        },
        handleReferenceClick() {
            if (!this.isTooltipVisible) {
                this.showTooltip()
            }
        },
        handleMouseLeave() {
            this.clearHoverTimer()

            if (this.mouseLeaveTimeout) {
                clearTimeout(this.mouseLeaveTimeout)
            }
            this.mouseLeaveTimeout = setTimeout(() => {
                if (!this.isMobile) {
                    this.hideTooltip()
                }
            }, 250)
        },
    },
}
